/**
 * Moduły włączane per spółka (T.01, 06.10.2026) — reguła po stronie frontu.
 *
 * Lustro `apps/api/src/companies/company-modules.ts`. Rozstrzyga zawsze
 * serwer: trasa modułu wyłączonego spółce odpowiada 403. Front tylko nie
 * pokazuje sekcji, z której i tak nic by nie przyszło — jak przy
 * uprawnieniach (`PermissionGate`), sekcja jest UKRYTA, nie wyszarzona.
 *
 * Stan modułów przychodzi z API w odpowiedzi `GET /companies` (pole
 * `modules`, już z zależnościami), więc nazw i zależności tu nie powtarzamy
 * — tylko klucze, żeby kod mógł je wskazać. Rozjazdu kluczy pilnuje test
 * (`modules.test.ts`), który czyta plik backendu.
 *
 * Różnica wobec uprawnień: rola jest znana od razu (z tokenu), a moduły
 * dopiero po odpowiedzi API. Do tego czasu — i gdy odpowiedź nie przyjdzie —
 * moduł uznajemy za włączony, bo tak jest domyślnie (brak wpisu = włączony),
 * a o dostępie i tak rozstrzyga serwer.
 *
 * Plik bez Reacta (czysta reguła — czyta ją też układ menu); hak
 * i komponent są w `ModuleGate.tsx`.
 */

export type ModuleKey =
  /** Rekomendowane pojazdy i zlecenia dla pojazdu. */
  | 'vehicleSuggestions'
  /** API dla Odysa / OneHub — bez ekranu we froncie. */
  | 'externalMatching'
  /** Kartoteka przewoźników i przewoźnicy z historią na relacji. */
  | 'carriers'
  /** Zapytania do przewoźników przy zleceniu Trade. */
  | 'carrierInquiries'
  /** Publikacja na giełdach i kolejka publikacji. */
  | 'exchanges'
  /** Harmonogram planowania kierowców (plik planistów). */
  | 'driverSchedule';

export const MODULE_KEYS: readonly ModuleKey[] = [
  'vehicleSuggestions',
  'externalMatching',
  'carriers',
  'carrierInquiries',
  'exchanges',
  'driverSchedule',
];

/** Stan modułu spółki — tak, jak go oddaje API. */
export interface CompanyModuleState {
  key: string;
  label: string;
  description: string;
  dependsOn: string[];
  /** Czy moduł działa — z zależnościami. */
  enabled: boolean;
  /** Ustawienie samej spółki. */
  ownEnabled: boolean;
  /** Wyłączone zależności. */
  disabledBy: string[];
  reason: string | null;
  changedAt: string | null;
  changedBy: { id: number; username: string } | null;
}

/** Spółka z modułami — z `GET /companies`. */
export interface CompanyWithModules {
  id: number;
  name?: string | null;
  modules?: CompanyModuleState[];
}

/** Moduły z rekordu spółki; brak pola (starsze API) = pusta lista. */
export function modulesOf(record: Record<string, unknown>): CompanyModuleState[] {
  const v = record.modules;
  return Array.isArray(v) ? (v as CompanyModuleState[]) : [];
}

/** Czy moduł działa w spółce. Brak informacji o module = włączony. */
export function companyModuleEnabled(company: CompanyWithModules, key: ModuleKey): boolean {
  const state = company.modules?.find((m) => m.key === key);
  return state ? state.enabled : true;
}

/**
 * Czy pokazać sekcję modułu — ta sama kolejność co w API: spółka rekordu,
 * potem spółka konta, a bez żadnej — czy moduł działa w którejkolwiek
 * spółce. `companies === null` = jeszcze nie wiemy, więc pokazujemy.
 */
export function moduleEnabledFor(
  companies: readonly CompanyWithModules[] | null,
  key: ModuleKey,
  companyId: number | null | undefined,
  userCompanyId: number | null | undefined,
): boolean {
  if (!companies) return true;
  for (const id of [companyId, userCompanyId]) {
    if (typeof id !== 'number') continue;
    const company = companies.find((c) => c.id === id);
    if (company) return companyModuleEnabled(company, key);
  }
  if (companies.length === 0) return true;
  return companies.some((c) => companyModuleEnabled(c, key));
}
